import styled, { css } from "styled-components";
import {
  Tab as TabPrimitive,
  TabList as TabListPrimitive,
} from "react-aria-components";
import { useDesignTokens } from "@boi/react-design-tokens";

export const Container = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { container } = tokens.tabs.desktop;

    return css`
      display: grid;
      gap: ${container.gap};
    `;
  }}
`;

export const TabList = styled(TabListPrimitive)`
  display: inline-grid;
  grid-auto-flow: column;
`;

export const Tab = styled(TabPrimitive)`
  position: relative;
  display: grid;
  grid-auto-flow: column;
  text-wrap: nowrap;
  cursor: pointer;
  outline: none;

  &[aria-disabled] {
    cursor: not-allowed;
  }
`;

export const TabFocusRing = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { tab } = tokens.tabs.desktop;

    return css`
      position: absolute;
      z-index: 2;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: none;
      border-top-left-radius: ${tab.focusRing.radius.topLeft};
      border-top-right-radius: ${tab.focusRing.radius.topRight};
      border-bottom-left-radius: ${tab.focusRing.radius.bottomLeft};
      border-bottom-right-radius: ${tab.focusRing.radius.bottomRight};

      ${Tab}[data-focus-visible="true"] && {
        display: block;
        outline-width: ${tab.focusRing.width};
        outline-style: ${tab.focusRing.style};
        outline-color: ${tab.focusRing.color};
        outline-offset: ${tab.focusRing.offset};
      }
    `;
  }}
`;

export const TabContentContainer = styled.div`
  position: relative;
`;

type TabContentProps = {
  $withIcon: boolean;
};

export const TabContent = styled.div<TabContentProps>`
  ${({ $withIcon }) => {
    const { tokens } = useDesignTokens();
    const { tab } = tokens.tabs.desktop;

    return [
      css`
        position: relative;
        z-index: 1;
        display: grid;
        place-items: start;
        grid-template-columns: 1fr;
        grid-auto-flow: column;
        gap: 16px;
        height: 100%;
        background: ${tab.backgroundColor.base};
        border-top-left-radius: ${tab.radius.topLeft};
        border-top-right-radius: ${tab.radius.topLeft};
        border-top-width: ${tab.strokeTop.width};
        border-top-style: ${tab.strokeTop.style};
        border-top-color: ${tab.strokeTop.color.base};
        padding-top: ${tab.padding.top};
        padding-bottom: ${tab.padding.bottom};
        padding-left: ${tab.padding.left.base};
        padding-right: ${tab.padding.right.base};

        ${Tab}[data-hovered="true"] && {
          background: ${tab.backgroundColor.hover};
        }

        ${Tab}[data-pressed="true"] && {
          outline-width: ${tab.activeRing.width};
          outline-style: ${tab.activeRing.style};
          outline-color: ${tab.activeRing.color};
          outline-offset: ${tab.activeRing.offset};
        }

        ${Tab}[aria-selected="true"] && {
          background: ${tab.backgroundColor.active};
          border-top-color: ${tab.strokeTop.color.active};

          &:after {
            content: "";
            position: absolute;
            left: 0;
            bottom: -1px;
            width: 100%;
            height: 1px;
            background: ${tab.backgroundColor.active};
          }
        }
      `,
      $withIcon &&
        css`
          padding-left: ${tab.padding.left.withIcon};
          padding-right: ${tab.padding.right.withIcon};
        `,
    ];
  }}
`;

export const TabLabelWrapper = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { tab } = tokens.tabs.desktop;

    return css`
      color: ${tab.label.color.base};

      ${Tab}[aria-selected="true"] && {
        color: ${tab.label.color.active};
      }

      ${Tab}:hover && {
        color: ${tab.label.color.hover};

        &[aria-selected] {
          color: ${tab.label.color.active};
        }
      }

      ${Tab}[aria-disabled] && {
        color: ${tab.label.color.disabled};

        &[aria-selected] {
          color: ${tab.label.color.disabled};
        }
      }
    `;
  }}
`;

export const TabSecondaryLabelWrapper = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { tab } = tokens.tabs.desktop;

    return css`
      color: ${tab.secondaryLabel.color.base};

      ${Tab}[aria-disabled="true"] && {
        color: ${tab.secondaryLabel.color.disabled};
      }
    `;
  }}
`;

export const TabIconWrapper = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { tab } = tokens.tabs.desktop;

    return css`
      display: grid;
      place-items: center;

      svg {
        width: ${tab.icon.width};
        height: ${tab.icon.height};
        color: ${tab.icon.color.base};
      }

      ${Tab}[aria-selected="true"] && {
        svg {
          color: ${tab.icon.color.active};
        }
      }

      ${Tab}:hover && {
        svg {
          color: ${tab.icon.color.hover};
        }

        &[aria-selected] {
          svg {
            color: ${tab.icon.color.active};
          }
        }
      }

      ${Tab}[aria-disabled="true"] && {
        svg {
          color: ${tab.icon.color.disabled};
        }

        &[aria-selected] {
          svg {
            color: ${tab.icon.color.disabled};
          }
        }
      }
    `;
  }}
`;

export const Separator = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { separator } = tokens.tabs.desktop.tab;

    return css`
      width: ${separator.width};
      background: ${separator.color};
      margin-top: ${separator.marginTop};
    `;
  }}
`;
